import React, { useState, useEffect } from 'react';
import { useSession } from '../Utils/SessionContext';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../Utils/ApiConfig';
import { usePopupClose } from '../Utils/usePopupClose';
import ChallengeQuestionCard from '../EvaluationComponents/Shards/Questions/ChallengeQuestionCard';
import { updateChallengeQuestions } from '../EvaluationComponents/Utils/QuestionUpdater';
import { CHALLENGE_QUESTION_TEMPLATES, QUESTION_TYPE_LABELS } from '../EvaluationComponents/Utils/QuestionTemplates';
import ComboBoxSelect from '../CreateEvaluationComponents/Shards/ComboBoxSelect';
import useSkillsAutocomplete from '../Utils/useSkillsAutocomplete';
import toast from 'react-hot-toast';

const AddFromLibrary = ({ isOpen, onClose, challengeId, currentQuestions, onQuestionsAdded, prefilledSkills = [] }) => {
  const { user, logout } = useSession();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [questions, setQuestions] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [selectedQuestions, setSelectedQuestions] = useState(new Set());
  const [isAdding, setIsAdding] = useState(false);
  
  // Convert prefilledSkills to the format expected by ComboBoxSelect
  const initialSkillsFilter = prefilledSkills.map(skill => ({
    value: skill.skill || skill,
    label: skill.skill || skill
  }));
  
  // Applied filter states (used for API calls)
  const [appliedFilterType, setAppliedFilterType] = useState([]);
  const [appliedFilterSkills, setAppliedFilterSkills] = useState(initialSkillsFilter);
  const [appliedSkillsFilterMod, setAppliedSkillsFilterMod] = useState('OR'); // 'AND' or 'OR'
  // const [appliedFilterBreadth, setAppliedFilterBreadth] = useState([]);
  
  // Pending filter states (user selections before applying)
  const [filterType, setFilterType] = useState([]);
  const [filterSkills, setFilterSkills] = useState(initialSkillsFilter);
  const [skillsFilterMod, setSkillsFilterMod] = useState('OR'); // 'AND' or 'OR'
  // const [filterBreadth, setFilterBreadth] = useState([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const ITEMS_PER_PAGE = 10;
  
  // Skills autocomplete hook
  const { skillsOptions, isLoadingSkills, searchSkills, clearSkills } = useSkillsAutocomplete();

  // Use popup close hook - close on ESC key only, not on click outside
  const popupRef = usePopupClose(isOpen, onClose, false, true);
  
  // Get all unique question types from templates
  const questionTypes = Object.keys(CHALLENGE_QUESTION_TEMPLATES).sort();
  
  // Complexity/Breadth options
  const complexityOptions = [
    { value: 'Focused', label: 'Simple' },
    { value: 'Involved', label: 'Medium' },
    { value: 'Comprehensive', label: 'Complex' }
  ];

  // Handle mounting/unmounting with animation
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Small delay to ensure DOM is ready for animation
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
      // Wait for animation to complete before unmounting
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Reset selections when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedQuestions(new Set());
    }
  }, [isOpen]);

  // Fetch questions when component opens or APPLIED filters change (reset to page 1)
  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
    }
  }, [isOpen, appliedFilterType, appliedFilterSkills]);

  // Fetch questions when page changes, modal opens, or APPLIED filters change
  useEffect(() => {
    if (isOpen) {
      fetchQuestions();
    }
  }, [currentPage, isOpen, appliedFilterType, appliedFilterSkills]);
  
  // Handle applying filters
  const handleApplyFilters = () => {
    setAppliedFilterType(filterType);
    setAppliedFilterSkills(filterSkills);
    setAppliedSkillsFilterMod(skillsFilterMod);
  };
  
  // Handle clearing filters
  const handleClearFilters = () => {
    setFilterType([]);
    setFilterSkills([]);
  };
  
  // Check if there are filter changes to apply
  const hasFilterChanges = () => {
    // Compare arrays by converting to JSON strings for simple comparison
    const typeChanged = JSON.stringify(filterType.map(f => f.value).sort()) !== 
                        JSON.stringify(appliedFilterType.map(f => f.value).sort());
    const skillsChanged = JSON.stringify(filterSkills.map(f => f.value).sort()) !== 
                          JSON.stringify(appliedFilterSkills.map(f => f.value).sort());
    const toggleChanged = skillsFilterMod !== appliedSkillsFilterMod;
    return typeChanged || skillsChanged || toggleChanged;
  };

  // Handle checkbox toggle
  const handleToggleQuestion = (libraryId) => {
    setSelectedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(libraryId)) {
        newSet.delete(libraryId);
      } else {
        newSet.add(libraryId);
      }
      return newSet;
    });
  };

  // Handle adding selected questions to challenge
  const handleAddSelectedQuestions = async () => {
    if (!challengeId || !currentQuestions) {
      toast.error('Missing challenge information. Please try again.');
      return;
    }

    try {
      // Get selected library questions
      const selectedItems = questions.filter(q => selectedQuestions.has(q.library_id));
      
      if (selectedItems.length === 0) {
        toast.error('No questions selected.');
        return;
      }
      
      setIsAdding(true);
      
      // Find max ID in current questions
      const maxId = Math.max(...currentQuestions.map(q => q.id || 0), 0);
      
      // Transform library questions to challenge format
      const newQuestions = selectedItems.map((item, index) => ({
        ...item.item_json,         // Copy all question data
        id: maxId + index + 1,     // New sequential ID
        uuid: crypto.randomUUID()  // New UUID
      }));
      
      // Combine with existing questions
      const updatedQuestions = [...currentQuestions, ...newQuestions];
      
      // Call API to update challenge
      await updateChallengeQuestions(challengeId, updatedQuestions, user);
      
      // Success handling
      toast.success(`Added ${newQuestions.length} question(s) to challenge!`);
      setSelectedQuestions(new Set()); // Clear selection
      onClose();                       // Close modal
      if (onQuestionsAdded) {
        onQuestionsAdded();            // Trigger refresh
      }
      
    } catch (error) {
      console.error('Error adding questions to challenge:', error);
      toast.error('Failed to add questions. Please try again.');
    } finally {
      setIsAdding(false);
    }
  };

  // Handle cancel selection
  const handleCancelSelection = () => {
    setSelectedQuestions(new Set());
  };

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Build query parameters for server-side filtering (using APPLIED filters)
      const params = new URLSearchParams();
      if (appliedFilterType.length > 0) {
        const types = appliedFilterType.map(t => t.value);
        params.append('type', JSON.stringify(types));
      }
      if (appliedFilterSkills.length > 0) {
        const skills = appliedFilterSkills.map(s => s.value);
        params.append('skills', JSON.stringify(skills));
        // Add skills filter mode (AND/OR)
        params.append('skills_filter_mod', appliedSkillsFilterMod);
      }
      // Breadth/Complexity filter is commented out for now
      // if (appliedFilterBreadth.length > 0) {
      //   appliedFilterBreadth.forEach(complexity => params.append('breadth', complexity.value));
      // }
      
      // Add pagination parameters
      const offset = (currentPage - 1) * ITEMS_PER_PAGE;
      params.append('offset', offset.toString());
      params.append('limit', ITEMS_PER_PAGE.toString());
      
      const url = `${API_ENDPOINTS.LIB_GET_CHALLENGE_QUESTIONS}${params.toString() ? `?${params.toString()}` : ''}`;
      
      // Make GET request to fetch library questions
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${user.access_token}`
        }
      });
      
      // Check if the response is ok
      if (!response.ok) {
        const error = new Error(`Server responded with status: ${response.status}`);
        error.status = response.status;
        throw error;
      }
      
      // Parse the response data
      const data = await response.json();
      console.log('Library questions response:', data);
      
      // Check if response has valid data
      const count = data[0]?.count || 0;
      // If count is 0, force items to empty array regardless of what's in the response
      const items = count === 0 ? [] : (data[0]?.items || []);
      
      console.log('Library questions loaded:', items);
      console.log('total results:', count);
      
      setQuestions(items);
      setTotalCount(count);
      
    } catch (err) {
      console.error('Error loading library questions:', err);
      
      // If it's a 401 error, it's an expired session - log out and redirect to login
      if (err.status === 401) {
        console.log('Session expired or unauthorized. Redirecting to login page.');
        logout();
        navigate('/login');
        return;
      }
      
      setError('Failed to load library questions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!shouldRender) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Overlay with blur */}
      <div 
        className={`fixed inset-0 bg-white transition-all duration-300 ${
          isAnimating ? 'backdrop-blur-sm bg-opacity-40' : 'bg-opacity-0'
        }`}
        onClick={onClose}
      />
      
      {/* Sliding Panel */}
      <div className="fixed inset-y-0 right-0 flex max-w-full">
        <div 
          ref={popupRef}
          className={`w-[85vw] transform transition-all duration-300 ease-in-out ${
            isAnimating ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="h-full flex flex-col bg-white shadow-2xl overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-gray-50 border-b border-gray-200 px-6 py-4 flex justify-between items-center z-20">
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold text-gray-700">Add from Library</h2>
                <span className="px-3 py-1 text-xs font-semibold text-white bg-red-500 rounded-full">
                  Alpha Feature
                </span>
              </div>
              <button 
                onClick={onClose}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Close"
              >
                <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Filter Bar - Full Width, Connected to Header */}
            <div className="sticky top-[73px] bg-gray-50 border-b border-gray-200 px-6 py-4 z-10">
              {/* Filters in one row */}
              <div className="flex gap-4 items-start mb-4">
                {/* Skills - flexible */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 h-9">
                    <label className="block text-base font-semibold text-gray-700">
                      Skills
                    </label>
                    {/* AND/OR Toggle */}
                    <div className="flex items-center gap-0.5 bg-white border border-gray-300 rounded p-0.5">
                      <button
                        onClick={() => setSkillsFilterMod('AND')}
                        className={`px-2 py-0.5 text-xs font-medium rounded transition-colors ${
                          skillsFilterMod === 'AND'
                            ? 'bg-purple-600 text-white'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        AND
                      </button>
                      <button
                        onClick={() => setSkillsFilterMod('OR')}
                        className={`px-2 py-0.5 text-xs font-medium rounded transition-colors ${
                          skillsFilterMod === 'OR'
                            ? 'bg-purple-600 text-white'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        OR
                      </button>
                    </div>
                  </div>
                  <ComboBoxSelect
                    value={filterSkills}
                    onChange={(selectedOptions) => setFilterSkills(selectedOptions || [])}
                    options={skillsOptions}
                    placeholder="Search skills..."
                    onInputChange={(inputValue, action) => {
                      if (action.action === 'input-change') {
                        if (inputValue && inputValue.length >= 2) {
                          searchSkills(inputValue);
                        } else {
                          clearSkills();
                        }
                      }
                    }}
                    isLoading={isLoadingSkills}
                    isMulti={true}
                    compact={true}
                  />
                </div>
                
                {/* Question Type - flexible */}
                <div className="flex-1">
                  <div className="mb-2 h-9 flex items-center">
                    <label className="block text-base font-semibold text-gray-700">
                      Question Type
                    </label>
                  </div>
                  <ComboBoxSelect
                    value={filterType}
                    onChange={(selectedOptions) => setFilterType(selectedOptions || [])}
                    options={questionTypes.map(type => ({
                      value: type,
                      label: QUESTION_TYPE_LABELS[type] || type
                    }))}
                    placeholder="Search types..."
                    isMulti={true}
                    compact={true}
                  />
                </div>
              </div>

              {/* Action buttons - centered at bottom, only show when there are changes */}
              {hasFilterChanges() && (
                <div className="flex justify-center gap-3">
                  <button
                    onClick={handleClearFilters}
                    className="px-6 py-2 bg-white text-gray-700 border border-gray-300 font-semibold rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Clear Filters
                  </button>
                  <button
                    onClick={handleApplyFilters}
                    className="px-8 py-2 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                    Apply
                  </button>
                </div>
              )}
            </div>

            {/* Content */}
            <div className={`p-8 space-y-6 overflow-auto ${selectedQuestions.size > 0 ? 'pb-32' : ''}`}>
              {loading && (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
                    <p className="text-lg font-semibold text-gray-900">Loading library questions...</p>
                  </div>
                </div>
              )}
              
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-800">{error}</p>
                  <button
                    onClick={fetchQuestions}
                    className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                  >
                    Retry
                  </button>
                </div>
              )}
              
              {!loading && !error && questions && (
                <div>
                  {/* Questions List - Full View */}
                  {questions.length > 0 ? (
                    <div className="space-y-4 mb-6">
                      {questions.map((item, index) => {
                      const isSelected = selectedQuestions.has(item.library_id);
                      return (
                        <div 
                          key={item.library_id}
                          className="relative group cursor-pointer"
                          onClick={() => handleToggleQuestion(item.library_id)}
                        >
                          {/* Checkbox - centered vertically */}
                          <div 
                            className="absolute top-1/2 -translate-y-1/2 left-0 z-20 transition-transform duration-200 group-hover:scale-125"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleQuestion(item.library_id)}
                              className="w-8 h-8 rounded border-2 border-purple-500 text-purple-600 focus:ring-purple-500 focus:ring-2 cursor-pointer accent-purple-600"
                            />
                          </div>
                          
                          {/* Question Card */}
                          <div className="pl-16">
                            <style>{`
                              .selected-card .group {
                                border-color: #9333EA !important;
                              }
                            `}</style>
                            <div className={isSelected ? 'selected-card' : ''}>
                              <ChallengeQuestionCard
                                question={{
                                  ...item.item_json,
                                  library_id: item.library_id,
                                  item_status: item.item_status,
                                  item_author: item.item_author
                                }}
                                index={index}
                                showAnswers={false}
                                onRegenerateQuestion={() => {}}
                                onLikeQuestion={() => {}}
                                onEditQuestion={null}
                                onDeleteQuestion={null}
                                onMoveUp={null}
                                onMoveDown={null}
                                totalQuestions={questions.length}
                                challengeId={null}
                                readOnly={true}
                                onAddToChallenge={null}
                              />
                            </div>
                          </div>
                        </div>
                      );
                      })}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-20">
                      <div className="mb-6">
                        <svg className="w-24 h-24 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">No Questions Found</h3>
                      <p className="text-gray-600 mb-6 max-w-md text-center">
                        {(appliedFilterType.length > 0 || appliedFilterSkills.length > 0) ? (
                          <>No questions match the selected filters. Try adjusting your filter criteria to find more results.</>
                        ) : (
                          <>The library is currently empty. Questions will appear here once they are added to the library.</>
                        )}
                      </p>
                      {(appliedFilterType.length > 0 || appliedFilterSkills.length > 0) && (
                        <button
                          onClick={() => {
                            setFilterType([]);
                            setFilterSkills([]);
                            setAppliedFilterType([]);
                            setAppliedFilterSkills([]);
                          }}
                          className="px-6 py-2 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Clear All Filters
                        </button>
                      )}
                    </div>
                  )}
                  
                  {/* Pagination Controls - At the end */}
                  {totalCount > 0 && (
                    <div className="flex items-center justify-between">
                      {/* Results info */}
                      <div className="text-sm text-gray-600">
                        Showing {Math.min((currentPage - 1) * ITEMS_PER_PAGE + 1, totalCount)}-{Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} of {totalCount} question{totalCount !== 1 ? 's' : ''}
                      </div>
                      
                      {/* Pagination buttons */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
                        >
                          Previous
                        </button>
                        
                        {/* Page numbers */}
                        <div className="flex gap-1">
                          {(() => {
                            const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);
                            const pages = [];
                            const maxVisible = 5;
                            
                            let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                            let endPage = Math.min(totalPages, startPage + maxVisible - 1);
                            
                            if (endPage - startPage + 1 < maxVisible) {
                              startPage = Math.max(1, endPage - maxVisible + 1);
                            }
                            
                            for (let i = startPage; i <= endPage; i++) {
                              pages.push(
                                <button
                                  key={i}
                                  onClick={() => setCurrentPage(i)}
                                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                    currentPage === i
                                      ? 'bg-purple-600 text-white'
                                      : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                  }`}
                                >
                                  {i}
                                </button>
                              );
                            }
                            
                            return pages;
                          })()}
                        </div>
                        
                        <button
                          onClick={() => setCurrentPage(prev => Math.min(Math.ceil(totalCount / ITEMS_PER_PAGE), prev + 1))}
                          disabled={currentPage >= Math.ceil(totalCount / ITEMS_PER_PAGE)}
                          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Bottom Action Bar - Shows when questions are selected */}
            {selectedQuestions.size > 0 && (
              <div className="fixed bottom-0 right-0 w-[85vw] bg-white border-t-2 border-purple-600 px-6 py-4 shadow-lg z-30">
                <div className="flex justify-between items-center">
                  <div className="text-gray-700 font-medium">
                    {selectedQuestions.size} question{selectedQuestions.size !== 1 ? 's' : ''} selected
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={handleCancelSelection}
                      className="px-6 py-2 bg-white text-gray-700 border-2 border-gray-300 font-semibold rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Clear Selection
                    </button>
                    <button
                      onClick={handleAddSelectedQuestions}
                      disabled={isAdding}
                      className="px-6 py-2 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {isAdding ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Adding...
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Add {selectedQuestions.size} Question{selectedQuestions.size !== 1 ? 's' : ''}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddFromLibrary;
